// AWS WAF CAPTCHA-style step-up for the Livi FR/UK signup SMS flow.
// Contract (PAW-2674):
//
// On a flagged request, kry-view-layer responds `428` with
// `{ step_up: { type: "waf_challenge", retry_path, retry_method }, vl_error }`.
// AWS WAF's own Challenge action returns HTTP `202`, which `request()`'s
// `response.ok` check (200-299) would treat as a *success* if we naively
// retried and accepted any 2xx — landing the user on the SMS-code screen
// with no SMS actually sent. To make that unreachable: mint a WAF token
// *before* retrying (never "send, get challenged, then solve"), attach it
// via the `x-aws-waf-token` header, and only accept the retry if its body is
// the exact empty object the alias route returns on real success.
import request, { IRequestOptions } from './request';
import RequestError from './requestError';
import environment from './environment';
import { logWarning } from './remoteLogger';

const LOG_TAG = 'stepUpChallenge';

const SMS_STEP_UP_PATH = '/api/view/register/device/sms/challenge';
const SMS_STEP_UP_METHOD = 'POST';

const SCRIPT_LOAD_TIMEOUT_MS = 5000;

declare global {
  interface Window {
    AwsWafIntegration?: {
      getToken: () => Promise<string>;
    };
  }
}

// Gated on COUNTRY, not brand (IS_LIVI): the AWS WAF Web ACL is associated
// with the region's shared ALB (prod-fr serves both livi.fr and kry.fr on
// the same backend_platform module), so a flagged request gets challenged
// regardless of which brand's domain it came in on.
export const isStepUpEnabled = (): boolean =>
  (environment.COUNTRY === 'FR' || environment.COUNTRY === 'GB') &&
  !!environment.WAF_CHALLENGE_SCRIPT_URL;

let wafSdkPromise: Promise<void> | undefined;

// Injects the AWS WAF Application Integration script tag once and memoizes
// the promise — a second call returns the same promise rather than
// injecting a second `<script>` tag.
export const loadWafSdk = (): Promise<void> => {
  if (wafSdkPromise) {
    return wafSdkPromise;
  }

  const scriptUrl = environment.WAF_CHALLENGE_SCRIPT_URL;

  if (!scriptUrl) {
    wafSdkPromise = Promise.reject(
      new Error('No AWS WAF challenge script URL configured for this market')
    );
    return wafSdkPromise;
  }

  wafSdkPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;

    const timeoutId = setTimeout(() => {
      // Clear the memoized promise so a later call retries the script load
      // instead of replaying this same rejection for the rest of the session.
      wafSdkPromise = undefined;
      reject(new Error('Timed out loading the AWS WAF challenge script'));
    }, SCRIPT_LOAD_TIMEOUT_MS);

    script.onload = () => {
      clearTimeout(timeoutId);
      resolve();
    };

    script.onerror = () => {
      clearTimeout(timeoutId);
      wafSdkPromise = undefined;
      reject(new Error('Failed to load the AWS WAF challenge script'));
    };

    document.head.appendChild(script);
  });

  return wafSdkPromise;
};

interface IStepUpBody {
  stepUp?: {
    type?: string;
    retryPath?: string;
    retryMethod?: string;
  };
}

// Retries against the alias path only after minting a WAF token, and only
// treats it as solved if the body is the empty object the alias returns on
// success — never a bare 2xx (see file header).
const retryWithWafToken = async <T>(
  originalOptions: IRequestOptions
): Promise<T> => {
  await loadWafSdk();
  const token = await window.AwsWafIntegration?.getToken();

  if (!token) {
    throw new Error('AWS WAF integration did not return a token');
  }

  const retryResult = await request<unknown>(SMS_STEP_UP_PATH, {
    ...originalOptions,
    method: SMS_STEP_UP_METHOD,
    headers: { ...originalOptions.headers, 'x-aws-waf-token': token },
  });

  const isExpectedEmptyBody =
    typeof retryResult === 'object' &&
    retryResult !== null &&
    !Array.isArray(retryResult) &&
    Object.keys(retryResult).length === 0;

  if (!isExpectedEmptyBody) {
    throw new Error(
      'Step-up retry did not return the expected empty response body'
    );
  }

  return retryResult as T;
};

// Wraps `request()` with AWS WAF step-up handling. On success, behaves
// identically to `request()`. On a `428` whose body carries a recognised
// `step_up.type === "waf_challenge"`, mints a token and retries once against
// `step_up.retry_path`; any failure along that path rethrows the *original*
// error unchanged, so the existing `useRequestError` alert still fires.
export const requestWithStepUp = async <T>(
  url: string,
  options: IRequestOptions = {}
): Promise<T> => {
  try {
    return await request<T>(url, options);
  } catch (error) {
    if (
      !isStepUpEnabled() ||
      !(error instanceof RequestError) ||
      error.status !== 428
    ) {
      throw error;
    }

    const stepUp = (error.body as IStepUpBody | undefined)?.stepUp;

    if (stepUp?.type !== 'waf_challenge') {
      throw error;
    }

    if (
      stepUp.retryPath !== SMS_STEP_UP_PATH ||
      stepUp.retryMethod !== SMS_STEP_UP_METHOD
    ) {
      logWarning(LOG_TAG, 'Unexpected step-up retry contract', {
        retryPath: String(stepUp.retryPath),
        retryMethod: String(stepUp.retryMethod),
      });
      throw error;
    }

    try {
      return await retryWithWafToken<T>(options);
    } catch (retryError) {
      logWarning(
        LOG_TAG,
        'Failed to resolve AWS WAF step-up challenge',
        retryError instanceof Error ? retryError : undefined
      );
      // Never fall open on a failed step-up: surface the original 428 so
      // the user gets the localized `vl_error` alert, not a silent no-op.
      throw error;
    }
  }
};
